import { useSubpantalla } from '../ui/navegacionReact';
import { useCallback, useEffect, useState } from 'react';
import { Download, Loader2, Settings2 } from 'lucide-react';
import { api } from '../api';
import { guardarArchivo } from '../ui/descarga';
import { useAvisos } from '../lib/contextoAvisos';
import Ayuda from '../ui/Ayuda';
import NotaExplicativa from '../ui/NotaExplicativa';
import Motivo from '../experiencia/Motivo';

/* =====================================================================
   Configuración comercial · ERP-OPERATIVO EC1 (etapas) y EC4 (campos y
   reportes)
   =====================================================================
   Tres bloques en una pantalla: las etapas del pipeline, los campos
   extra por entidad y los reportes armados con columnas y filtros.
   Nada se borra: retirar es apagar.
   ===================================================================== */
export default function ConfiguracionComercial() {
  const avisos = useAvisos();
  const [tick, setTick] = useState(0);
  const recargar = useCallback(() => setTick((t) => t + 1), []);
  const [etapas, setEtapas] = useState(null);
  const [campos, setCampos] = useState([]);
  const [rep, setRep] = useState({ reportes: [], entidades: {}, operadores: [] });
  const [error, setError] = useState('');

  useEffect(() => {
    let vivo = true;
    Promise.all([
      api.get('/comercial/pipeline/etapas'), api.get('/comercial/campos'), api.get('/comercial/reportes'),
    ]).then(([e, c, r]) => { if (vivo) { setEtapas(e); setCampos(c); setRep(r); setError(''); } })
      .catch((err) => { if (vivo) setError(err.message); });
    return () => { vivo = false; };
  }, [tick]);

  async function accion(fn) {
    try { const r = await fn(); avisos.exito(r.mensaje || 'Guardado.'); recargar(); }
    catch (e) { avisos.error(e.message); }
  }

  if (error && !etapas) return <p className="p-4 text-sm text-red-700">{error}</p>;
  if (!etapas) {
    return (
      <p className="flex items-center gap-2 p-4 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Leyendo la configuración…
      </p>
    );
  }

  return (
    <div className="flex max-w-5xl flex-col gap-6 p-4">
      <header>
        <h1 className="text-xl font-semibold text-gray-900">Configuración comercial</h1>
        <p className="text-gray-600">Tus etapas, tus campos y tus reportes. Sin programar.</p>
        <Ayuda id="comercial.configurar.pantalla" />
      </header>

      <Etapas etapas={etapas} accion={accion} />
      <Campos campos={campos} entidades={Object.keys(rep.entidades || {})} accion={accion} />
      <Reportes rep={rep} accion={accion} />
    </div>
  );
}

function Etapas({ etapas, accion }) {
  const [f, setF] = useState({ clave: '', nombre: '', orden: '', cierra: '' });
  const listo = f.clave.trim().length >= 2 && f.nombre.trim().length >= 2;
  return (
    <section className="rounded-xl border border-gray-200 p-4">
      <h2 className="flex items-center gap-2 font-semibold text-gray-900">
        <Settings2 className="h-4 w-4" aria-hidden="true" /> Etapas del embudo de ventas
      </h2>
      <p className="text-sm text-gray-600">Por dónde pasa un prospecto. Una etapa «gana» o «pierde»; las demás son camino.</p>
      <ul className="mt-2 flex flex-wrap gap-2 text-sm">
        {etapas.map((e) => (
          <li key={e.clave} className={`rounded-lg border px-2 py-1 ${e.activo ? 'border-gray-200' : 'border-dashed border-gray-300 text-gray-400'}`}>
            {e.orden}. {e.nombre} <span className="font-mono text-xs text-gray-500">{e.clave}</span>
            {e.cierra && <span className="ml-1 text-xs text-gray-500">({e.cierra === 'GANADO' ? 'gana' : 'pierde'})</span>}
          </li>
        ))}
      </ul>
      <form onSubmit={(ev) => { ev.preventDefault(); accion(() => api.put('/comercial/pipeline/etapas', { clave: f.clave.trim().toUpperCase(), nombre: f.nombre.trim(), orden: Number(f.orden || 0), cierra: f.cierra || null })).then(() => setF({ clave: '', nombre: '', orden: '', cierra: '' })); }}
            className="mt-3 grid gap-2 sm:grid-cols-5">
        <input value={f.clave} onChange={(e) => setF({ ...f, clave: e.target.value })} placeholder="Clave (p. ej. DEMO)" className="rounded-lg border border-gray-300 p-2 text-sm" />
        <input value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} placeholder="Nombre" className="rounded-lg border border-gray-300 p-2 text-sm" />
        <input type="number" value={f.orden} onChange={(e) => setF({ ...f, orden: e.target.value })} placeholder="Orden" className="rounded-lg border border-gray-300 p-2 text-sm" />
        <select value={f.cierra} onChange={(e) => setF({ ...f, cierra: e.target.value })} className="rounded-lg border border-gray-300 p-2 text-sm">
          <option value="">Es camino</option><option value="GANADO">Gana</option><option value="PERDIDO">Pierde</option>
        </select>
        <button type="submit" disabled={!listo} aria-describedby={listo ? undefined : 'motivo-etapa'} className="rounded-xl bg-[var(--color-marca)] px-3 py-2 text-sm font-semibold text-white disabled:bg-gray-300">Guardar etapa</button>
        <Motivo id="motivo-etapa" cuando={!listo}>Falta la clave y el nombre de la etapa (mínimo 2 letras cada uno).</Motivo>
      </form>
      {!listo && <p className="mt-1 text-xs text-amber-700">Clave y nombre de al menos dos letras. Una clave que ya existe se actualiza, no se duplica.</p>}
    </section>
  );
}

function Campos({ campos, entidades, accion }) {
  const [f, setF] = useState({ entidad: entidades[0] || 'cliente', clave: '', etiqueta: '', tipo: 'texto', opciones: '', obligatorio: false });
  const listo = f.clave.trim().length >= 2 && f.etiqueta.trim();
  return (
    <section className="rounded-xl border border-gray-200 p-4">
      <h2 className="font-semibold text-gray-900">Campos extra</h2>
      <p className="text-sm text-gray-600">Lo que TEGO no pregunta y tú necesitas: un dato más en clientes, productos, proveedores o prospectos.</p>
      <Ayuda id="comercial.configurar.campo_extra" />
      {campos.length === 0 ? (
        <NotaExplicativa titulo="Todavía no hay campos extra">Añade el primero abajo. Aparece en la ficha de esa entidad al instante.</NotaExplicativa>
      ) : (
        <ul className="mt-2 divide-y divide-gray-100 text-sm">
          {campos.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-2 py-1">
              <span className="rounded bg-gray-100 px-2 text-xs">{c.entidad}</span>
              <span className="font-medium">{c.etiqueta}</span>
              <span className="font-mono text-xs text-gray-500">{c.clave} · {c.tipo}</span>
              {c.obligatorio && <span className="text-xs text-amber-700">obligatorio</span>}
              <button type="button" onClick={() => accion(() => api.post(`/comercial/campos/${c.entidad}/${c.clave}/retirar`))}
                      className="ml-auto text-xs text-gray-600">Retirar</button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={(ev) => { ev.preventDefault(); accion(() => api.put('/comercial/campos', { entidad: f.entidad, clave: f.clave.trim().toLowerCase(), etiqueta: f.etiqueta.trim(), tipo: f.tipo, opciones: f.tipo === 'opcion' ? f.opciones.split(',').map((s) => s.trim()).filter(Boolean) : null, obligatorio: f.obligatorio })).then(() => setF({ ...f, clave: '', etiqueta: '', opciones: '' })); }}
            className="mt-3 grid gap-2 sm:grid-cols-6">
        <select value={f.entidad} onChange={(e) => setF({ ...f, entidad: e.target.value })} className="rounded-lg border border-gray-300 p-2 text-sm">
          {(entidades.length ? entidades : ['cliente']).map((e) => <option key={e}>{e}</option>)}
        </select>
        <input value={f.clave} onChange={(e) => setF({ ...f, clave: e.target.value })} placeholder="Clave (p. ej. ruta)" className="rounded-lg border border-gray-300 p-2 text-sm" />
        <input value={f.etiqueta} onChange={(e) => setF({ ...f, etiqueta: e.target.value })} placeholder="Etiqueta que se ve" className="rounded-lg border border-gray-300 p-2 text-sm" />
        <select value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })} className="rounded-lg border border-gray-300 p-2 text-sm">
          {['texto', 'numero', 'fecha', 'opcion', 'si_no'].map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        {f.tipo === 'opcion'
          ? <input value={f.opciones} onChange={(e) => setF({ ...f, opciones: e.target.value })} placeholder="Opciones, separadas por coma" className="rounded-lg border border-gray-300 p-2 text-sm" />
          : <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={f.obligatorio} onChange={(e) => setF({ ...f, obligatorio: e.target.checked })} /> Obligatorio</label>}
        <button type="submit" disabled={!listo} aria-describedby={listo ? undefined : 'motivo-campo'} className="rounded-xl bg-[var(--color-marca)] px-3 py-2 text-sm font-semibold text-white disabled:bg-gray-300">Guardar campo</button>
        <Motivo id="motivo-campo" cuando={!listo}>Falta la clave y la etiqueta del campo (la clave, mínimo 2 letras).</Motivo>
      </form>
    </section>
  );
}

function Reportes({ rep, accion }) {
  const entidades = Object.keys(rep.entidades || {});
  const [f, setF] = useState({ nombre: '', entidad: entidades[0] || '', columnas: [], filtro: { campo: '', op: 'eq', valor: '' } });
  const [vista, setVista] = useState(null);
  useSubpantalla('vista', !!vista, () => setVista(null), { reemplaza: true });
  const columnasDe = rep.entidades?.[f.entidad] || [];
  const listo = f.nombre.trim().length >= 2 && f.entidad && f.columnas.length > 0;
  const alternar = (c) => setF({ ...f, columnas: f.columnas.includes(c) ? f.columnas.filter((x) => x !== c) : [...f.columnas, c] });
  async function ver(id) {
    try { setVista(await api.get(`/comercial/reportes/${id}/datos`)); } catch (e) { setVista({ error: e.message }); }
  }
  return (
    <section className="rounded-xl border border-gray-200 p-4">
      <h2 className="font-semibold text-gray-900">Reportes</h2>
      <p className="text-sm text-gray-600">Eliges columnas y filtros; TEGO arma la tabla y te la da en Excel.</p>
      <Ayuda id="comercial.configurar.reporte" />
      {rep.reportes.length === 0 ? (
        <NotaExplicativa titulo="Todavía no hay reportes guardados">Arma el primero abajo: nombre, de qué, qué columnas. Queda guardado para volver a pedirlo.</NotaExplicativa>
      ) : (
        <ul className="mt-2 divide-y divide-gray-100 text-sm">
          {rep.reportes.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-2 py-1">
              <span className="font-medium">{r.nombre}</span>
              <span className="rounded bg-gray-100 px-2 text-xs">{r.entidad}</span>
              <span className="text-xs text-gray-500">{(r.columnas || []).join(', ')}</span>
              <button type="button" onClick={() => ver(r.id)} className="ml-auto text-xs font-semibold text-[var(--color-marca)]">Ver</button>
              <button type="button" onClick={async () => { try { guardarArchivo(await api.bajar(`/comercial/reportes/${r.id}/hoja.xlsx`), `${r.nombre}.xlsx`); } catch (e) { setVista({ error: e.message }); } }}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--color-marca)]">
                <Download className="h-3 w-3" aria-hidden="true" /> Excel
              </button>
              <button type="button" onClick={() => accion(() => api.post(`/comercial/reportes/${r.id}/retirar`))} className="text-xs text-gray-600">Retirar</button>
            </li>
          ))}
        </ul>
      )}
      {vista && (
        <div className="mt-2 overflow-x-auto rounded-lg bg-gray-50 p-2 text-xs">
          {vista.error ? <p className="text-red-700">{vista.error}</p> : (
            <table className="min-w-full">
              <thead><tr>{vista.reporte.columnas.map((c) => <th key={c} className="px-2 text-left">{c.replace('extra:', '')}</th>)}</tr></thead>
              <tbody>{vista.filas.map((fila, i) => <tr key={i}>{vista.reporte.columnas.map((c) => <td key={c} className="px-2">{String(fila[c] ?? '')}</td>)}</tr>)}</tbody>
            </table>
          )}
          {vista.orden_neutro && <p className="text-amber-700">Ordenado por código o nombre. El orden por costo se ve con el permiso «Ver el valor a costo».</p>}
          {vista.cortado && <p className="text-amber-700">Se muestran las primeras filas; el Excel las trae todas hasta el tope.</p>}
        </div>
      )}
      <form onSubmit={(ev) => { ev.preventDefault(); const filtros = f.filtro.campo ? [{ campo: f.filtro.campo, op: f.filtro.op, valor: f.filtro.valor }] : []; accion(() => api.put('/comercial/reportes', { nombre: f.nombre.trim(), entidad: f.entidad, columnas: f.columnas, filtros })).then(() => setF({ ...f, nombre: '', columnas: [] })); }}
            className="mt-3 flex flex-col gap-2">
        <div className="grid gap-2 sm:grid-cols-3">
          <input value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} placeholder="Nombre del reporte" className="rounded-lg border border-gray-300 p-2 text-sm" />
          <select value={f.entidad} onChange={(e) => setF({ ...f, entidad: e.target.value, columnas: [] })} className="rounded-lg border border-gray-300 p-2 text-sm">
            {entidades.map((e) => <option key={e}>{e}</option>)}
          </select>
          <div className="flex gap-1">
            <select value={f.filtro.campo} onChange={(e) => setF({ ...f, filtro: { ...f.filtro, campo: e.target.value } })} className="flex-1 rounded-lg border border-gray-300 p-2 text-sm">
              <option value="">Sin filtro</option>{columnasDe.map((c) => <option key={c}>{c}</option>)}
            </select>
            <select value={f.filtro.op} onChange={(e) => setF({ ...f, filtro: { ...f.filtro, op: e.target.value } })} className="rounded-lg border border-gray-300 p-2 text-sm">
              {(rep.operadores || ['eq']).map((o) => <option key={o}>{o}</option>)}
            </select>
            <input value={f.filtro.valor} onChange={(e) => setF({ ...f, filtro: { ...f.filtro, valor: e.target.value } })} placeholder="Valor" className="w-24 rounded-lg border border-gray-300 p-2 text-sm" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 text-sm">
          {columnasDe.map((c) => (
            <label key={c} className={`rounded-lg border px-2 py-1 ${f.columnas.includes(c) ? 'border-[var(--color-marca)] bg-[var(--color-marca-suave)]' : 'border-gray-200'}`}>
              <input type="checkbox" className="mr-1" checked={f.columnas.includes(c)} onChange={() => alternar(c)} />{c.replace('extra:', '')}
            </label>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button type="submit" disabled={!listo} aria-describedby={listo ? undefined : 'motivo-reporte'} className="rounded-xl bg-[var(--color-marca)] px-3 py-2 text-sm font-semibold text-white disabled:bg-gray-300">Guardar reporte</button>
          <Motivo id="motivo-reporte" cuando={!listo}>Falta el nombre (mínimo 2 letras), la entidad y al menos una columna.</Motivo>
          {!listo && <span className="text-xs text-amber-700">Un nombre, una entidad y al menos una columna.</span>}
        </div>
      </form>
    </section>
  );
}
